import { useMemo, useState } from 'react'

import { Dialog } from '@buffer-mono/popcorn'

import { PostItemsFooter } from './PostItemsFooter'
import { PostItemsTable } from './PostItemsTable'
import { BulkPostDialogBody } from '../BulkPostDialog'
import {
  createPost,
  isError,
  isSuccess,
  isValid,
  parsePostingTime,
  type ListItem,
  updateItemStatus,
} from './helpers'
import { StatusCounters } from './StatusCounters'

export const PostItems = ({
  items,
  setItems,
  channelId,
  channelName,
  twentyFourHourFormat,
  channelTimezone,
  onDialogClose,
}: {
  items: ListItem[]
  setItems: React.Dispatch<React.SetStateAction<ListItem[]>>
  channelId: string
  channelName: string
  twentyFourHourFormat: boolean
  channelTimezone: string
  onDialogClose: (closeAction: 'success' | 'cancel') => void
}): JSX.Element => {
  const [creationAttempted, setCreationAttempted] = useState(false)
  const [isCreatingPosts, setIsCreatingPosts] = useState(false)

  const [selectedSaveAction, setSelectedSaveAction] = useState<
    'post' | 'draft'
  >('post')

  const validItems = useMemo(() => {
    return items.filter((item) => isValid(item))
  }, [items])
  const validItemCount = validItems.length

  const successItemCount = useMemo(() => {
    return items.filter((item) => isSuccess(item)).length
  }, [items])

  const errorItems = useMemo(
    () => items.filter((item) => isError(item)),
    [items],
  )

  const canStartPostCreation = useMemo(
    () => !creationAttempted && validItemCount > 0,
    [creationAttempted, validItemCount],
  )
  const isPostCreationComplete = useMemo(
    () =>
      creationAttempted &&
      items.every(
        (item) =>
          item.status !== 'loading' &&
          item.status !== 'valid' &&
          (item.status === 'error' || item.status === 'success'),
      ),
    [creationAttempted, items],
  )

  const onCreatePosts = async (type: 'post' | 'draft'): Promise<void> => {
    // Set whether items are being saved as drafts or posts
    setSelectedSaveAction(type)
    setIsCreatingPosts(true)

    // Set all invalid items to error status
    errorItems.forEach((item) => {
      setItems((items) => updateItemStatus(items, item.id, 'error'))
    })

    // Set all valid items to loading status
    validItems.forEach((item) => {
      setItems((items) => updateItemStatus(items, item.id, 'loading'))
    })

    for (const item of validItems) {
      try {
        // Parse posting time to scheduling data
        const schedulingData = item.data.postingTime
          ? parsePostingTime(item.data.postingTime, channelTimezone)
          : {}
        // Remove postingTime from the API payload
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { postingTime, ...postData } = item.data

        const post = {
          ...postData,
          is_draft: type === 'draft',
          ...schedulingData,
        }
        const response = await createPost(post)
        if (!response.success) {
          throw new Error(response.message)
        }
        setItems((items) => updateItemStatus(items, item.id, 'success'))
      } catch (error) {
        setItems((items) =>
          updateItemStatus(items, item.id, 'error', [
            error instanceof Error ? error.message : 'Unknown error',
          ]),
        )
      }
    }

    setIsCreatingPosts(false)

    // Set creation attempted to true
    setCreationAttempted(true)
  }

  const title = useMemo(() => {
    if (isPostCreationComplete) {
      if (successItemCount === 0) {
        return `Failed to create ${
          selectedSaveAction === 'draft' ? 'drafts' : 'posts'
        }`
      }
      return selectedSaveAction === 'draft'
        ? 'Saving Complete!'
        : 'Scheduling Complete!'
    }
    if (isCreatingPosts) {
      return selectedSaveAction === 'draft'
        ? 'Saving content...'
        : 'Scheduling content...'
    }
    return 'Review Content'
  }, [
    isPostCreationComplete,
    isCreatingPosts,
    successItemCount,
    selectedSaveAction,
  ])

  return (
    <>
      <BulkPostDialogBody title={title}>
        <StatusCounters
          items={items}
          isCreatingPosts={isCreatingPosts}
          isPostCreationComplete={isPostCreationComplete}
          selectedSaveAction={selectedSaveAction}
        />
        <PostItemsTable
          items={items}
          twentyFourHourFormat={twentyFourHourFormat}
          channelTimezone={channelTimezone}
        />
      </BulkPostDialogBody>
      <Dialog.Separator />
      <PostItemsFooter
        channelId={channelId}
        channelName={channelName}
        successItemCount={successItemCount}
        validItemCount={validItemCount}
        selectedSaveAction={selectedSaveAction}
        canStartPostCreation={canStartPostCreation}
        isCreatingPosts={isCreatingPosts}
        isPostCreationComplete={isPostCreationComplete}
        onCreatePosts={(postType) => {
          onCreatePosts(postType).catch(() => null)
        }}
        onDialogClose={onDialogClose}
      />
    </>
  )
}
